import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { ApiError } from "@/lib/api";

const PREVIEW_ROWS = [
  { category: "IT Services", status: "Active", bg: "bg-secondary-50", text: "text-secondary-700", value: "R 2.4M" },
  { category: "Facilities", status: "Decision Pending", bg: "bg-accent-50", text: "text-accent-700", value: "R 860K" },
  { category: "Security", status: "Active", bg: "bg-secondary-50", text: "text-secondary-700", value: "R 1.1M" },
];

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Unable to connect to server. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-100 flex items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="w-full max-w-6xl bg-white rounded-3xl shadow-xl shadow-foreground-900/5 overflow-hidden lg:grid lg:grid-cols-2">

        {/* ── Left: Form Panel ─────────────────────────────────────── */}
        <div className="flex flex-col p-8 sm:p-12 lg:p-14">
          <img src="/logo.svg" alt="SupplyGuard" className="h-[82px] w-auto self-center mb-10 sm:mb-14" />

          <div className="w-full max-w-sm mx-auto lg:mx-0 flex-1 flex flex-col justify-center">
            <h2 className="text-foreground-900 text-3xl font-bold mb-2">Welcome back</h2>
            <p className="text-foreground-500 text-sm mb-8">Sign in to access the SupplyGuard portal.</p>

            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-foreground-700 text-sm font-medium mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@department.gov.za"
                  required
                  className="w-full h-12 px-4 bg-white border border-background-300 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all"
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-foreground-700 text-sm font-medium">
                    Password
                  </label>
                  <a href="#" className="text-primary-600 text-xs font-medium hover:text-primary-700 transition-colors">
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="min 8 chars"
                    required
                    className="w-full h-12 px-4 pr-11 bg-white border border-background-300 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors p-1 cursor-pointer"
                    tabIndex={-1}
                  >
                    <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}></i>
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2.5 bg-accent-50 border border-accent-200 rounded-lg px-3.5 py-3">
                  <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5 shrink-0"></i>
                  <p className="text-accent-700 text-sm leading-snug">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    <span>Authenticating…</span>
                  </>
                ) : (
                  <span>Sign in</span>
                )}
              </button>
            </form>

            <p className="text-foreground-400 text-xs text-center mt-6 leading-relaxed">
              Forgot your password? Contact your system administrator.
            </p>
            <p className="text-foreground-400 text-xs text-center mt-2 leading-relaxed">
              Don't have an account?{" "}
              <Link to="/register" className="text-primary-600 font-medium hover:text-primary-700">
                Sign up
              </Link>
            </p>
          </div>

          <p className="text-foreground-400 text-xs mt-10">
            © {new Date().getFullYear()} SupplyGuard, All Rights Reserved.
          </p>
        </div>

        {/* ── Right: Brand / Preview Panel ─────────────────────────── */}
        <div className="hidden lg:flex flex-col relative overflow-hidden bg-primary-600 p-12">

          {/* Soft glow accents */}
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 -left-16 w-64 h-64 rounded-full bg-primary-900/30 blur-3xl pointer-events-none" />

          <div className="relative z-10">
            <h1 className="text-white text-3xl font-bold leading-tight mb-3 max-w-sm">
              The simplest way to manage every government contract.
            </h1>
            <p className="text-primary-200 text-sm leading-relaxed max-w-xs">
              Real-time visibility into contracts, renewals, and supplier performance across your department.
            </p>
          </div>

          {/* Dashboard preview mockup */}
          <div className="relative z-10 mt-10 flex-1 flex items-start justify-center">
            <div className="w-full max-w-md">
              <div className="bg-white rounded-2xl shadow-2xl p-5">

                {/* Mock header */}
                <div className="flex items-center justify-between mb-5">
                  <p className="text-foreground-900 font-bold text-sm">Dashboard</p>
                  <div className="flex items-center gap-1.5 bg-background-100 rounded-full px-3 py-1">
                    <span className="text-foreground-600 text-xs font-medium">This Quarter</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 text-xs"></i>
                  </div>
                </div>

                {/* Stat tiles */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="bg-background-50 rounded-xl p-3.5">
                    <p className="text-foreground-500 text-[11px] font-medium mb-1.5">Active Contracts</p>
                    <p className="text-foreground-900 text-xl font-bold">128</p>
                    <p className="text-secondary-600 text-[11px] font-medium mt-1 flex items-center gap-1">
                      <i className="ri-arrow-up-line"></i> +6% this month
                    </p>
                  </div>
                  <div className="bg-background-50 rounded-xl p-3.5">
                    <p className="text-foreground-500 text-[11px] font-medium mb-1.5">Expiring ≤ 30 Days</p>
                    <p className="text-foreground-900 text-xl font-bold">14</p>
                    <p className="text-accent-600 text-[11px] font-medium mt-1 flex items-center gap-1">
                      <i className="ri-arrow-up-line"></i> +3 vs last month
                    </p>
                  </div>
                </div>

                {/* Mock table */}
                <p className="text-foreground-500 text-[11px] font-semibold uppercase tracking-wider mb-2">By Category</p>
                <div className="space-y-1">
                  {PREVIEW_ROWS.map((row) => (
                    <div key={row.category} className="flex items-center justify-between py-1.5">
                      <span className="text-foreground-700 text-xs font-medium">{row.category}</span>
                      <div className="flex items-center gap-2.5">
                        <span className={`${row.bg} ${row.text} text-[10px] font-semibold px-2 py-0.5 rounded-full`}>
                          {row.status}
                        </span>
                        <span className="text-foreground-800 text-xs font-semibold w-14 text-right">{row.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alert card, stacked below with a peek of offset for depth */}
              <div className="hidden xl:block w-52 ml-auto -mt-4 mr-4 bg-white rounded-xl shadow-2xl border border-background-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-foreground-900 text-xs font-bold">Renewal Alert</p>
                  <i className="ri-close-line text-foreground-400 text-xs"></i>
                </div>
                <p className="text-foreground-700 text-xs font-medium">Metro Security Services</p>
                <p className="text-accent-600 text-[11px] font-medium mb-3">Expires in 12 days</p>
                <button className="w-full h-7 bg-primary-600 text-white text-[11px] font-semibold rounded-md">
                  Notify Officer
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
